import type { HotelOfferCardModel } from '../types/hotels.presentation';
import { mergeHotelDetails } from './resolveDetailHotel';

function comparablePrice(offer: HotelOfferCardModel): number {
  return offer.totalPrice > 0 ? offer.totalPrice : Number.POSITIVE_INFINITY;
}

/**
 * Identity for deduplication. Suppliers assign different hotelIds to the same
 * physical hotel, so the normalized hotel name is the only cross-supplier
 * signal available. Falls back to hotelId when the name is missing or is the
 * hotelId itself (the mapper's fallback when no real name exists).
 */
function dedupKey(offer: HotelOfferCardModel): string {
  const name = offer.hotelName?.trim();
  if (!name || name === offer.hotelId) return `id:${offer.hotelId}`;

  const normalized = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  return normalized ? `name:${normalized}` : `id:${offer.hotelId}`;
}

/**
 * The cheapest card stays as the hotel's card ("from" price), but its detail keeps the room
 * options of every offer of that hotel so the user can pick any of them.
 */
function mergeCardDetails(
  winner: HotelOfferCardModel,
  loser: HotelOfferCardModel,
): HotelOfferCardModel {
  if (!winner.detail || !loser.detail) return winner;

  const detail = mergeHotelDetails(winner.detail, loser.detail);
  return {
    ...winner,
    availableMealTypes: detail.availableMealTypes,
    hasPromotion: detail.hasPromotion,
    detail,
  };
}

/**
 * Merges incoming stream offers into the current list, deduplicating hotels
 * repeated across suppliers/batches. The cheapest offer wins the card; the
 * detail gathers the room options of all of them. Offers without a usable
 * price never replace a priced one.
 */
export function mergeHotelOffers(
  current: HotelOfferCardModel[],
  incoming: HotelOfferCardModel[],
): HotelOfferCardModel[] {
  const merged = [...current];
  const indexByKey = new Map(current.map((offer, index) => [dedupKey(offer), index]));

  for (const offer of incoming) {
    const key = dedupKey(offer);
    const existingIndex = indexByKey.get(key);

    if (existingIndex === undefined) {
      indexByKey.set(key, merged.length);
      merged.push(offer);
      continue;
    }

    const existing = merged[existingIndex];
    const [winner, loser] =
      comparablePrice(offer) < comparablePrice(existing) ? [offer, existing] : [existing, offer];
    merged[existingIndex] = mergeCardDetails(winner, loser);
  }

  return merged;
}
